<script setup lang="ts">
import { useForm } from '@inertiajs/vue3'
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import InertiaFormField from '@/components/InertiaFormField.vue'
import InputBoolean from '@/components/inputs/InputBoolean.vue'
import FileUploadComponent from '@/components/ProfileImageUploader.vue'
import Button from 'primevue/button'
import DatePicker from 'primevue/datepicker'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import SectionHeading from '@/components/SectionHeading.vue'
import { reportWarning } from '@/utils/error-reporting'

const { t } = useI18n()

type UserProfileValue = string | number | boolean | Date | null | undefined

type UserProfileField = {
  name: string
  type?: 'file' | 'string' | 'date' | 'boolean' | 'enum'
}

type UserProfileOption = {
  label: string
  value: string
}

const props = defineProps<{
  user: {
    id: string
    profile_image_url?: string | null
    [key: string]: UserProfileValue
  }
  userSchema: UserProfileField[]
  languages: UserProfileOption[]
  userTypes: UserProfileOption[]
}>()

const userForm = useForm({
  ...props.user,
})

const savingText = ref(false)

/* Save profile fields */
function saveTextFields() {
  savingText.value = true

  userForm.patch(`/users/${props.user.id}`, {
    onSuccess: (page) => {
      const user = page.props.user as Record<string, UserProfileValue> | undefined

      if (!user) {
        return
      }

      userForm.defaults(user)
      userForm.reset()
    },
    onFinish: () => {
      savingText.value = false
    },
  })
}

const passwordForm = useForm({
  current_password: '',
  new_password: '',
  new_password_confirmation: '',
})

function submitNewPassword() {
  passwordForm.clearErrors()

  if (!passwordForm.current_password) {
    passwordForm.setError('current_password', t('warnings.current_password_required'))
    return
  }

  if (!passwordForm.new_password) {
    passwordForm.setError('new_password', t('warnings.new_password_required'))
    return
  }

  if (!passwordForm.new_password_confirmation) {
    passwordForm.setError('new_password_confirmation', t('warnings.password_confirmation_required'))
    return
  }

  if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
    passwordForm.setError('new_password_confirmation', t('warnings.passwords_do_not_match'))
    return
  }

  passwordForm.put(`/users/${props.user.id}/password`, {
    onSuccess: () => passwordForm.reset(),
  })
}

function fieldComponent(field: UserProfileField) {
  if (!field || !field.type) return InputText

  switch (field.type) {
    case 'file':
      return FileUploadComponent
    case 'string':
      return InputText
    case 'date':
      return DatePicker
    case 'boolean':
      return InputBoolean
    case 'enum':
      return Select
    default:
      reportWarning('Unknown user profile field type', { type: field.type, field: field.name })
      return InputText
  }
}

function enumOptions(field: UserProfileField) {
  if (field.name === 'language') return props.languages
  if (field.name === 'type') return props.userTypes
  return []
}
</script>

<template>
  <!-- Profile Image Form -->
  <div class="flex flex-col gap-8 rounded-xl border border-gray-200 bg-white p-18 shadow-sm">
    <SectionHeading class="mb-6">
      {{ t('titles.profile_image') }}
    </SectionHeading>

    <InertiaFormField>
      <FileUploadComponent :user-id="props.user.id" :current-image="props.user.profile_image_url ?? undefined" />
    </InertiaFormField>
  </div>

  <!-- User Profile Fields -->
  <div class="flex flex-col gap-8 rounded-xl border border-gray-200 bg-white p-18 shadow-sm">
    <SectionHeading class="mb-6">
      {{ t('titles.user_profile') }}
    </SectionHeading>

    <form @submit.prevent="saveTextFields">
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        <template v-for="field in props.userSchema" :key="field.name">
          <InertiaFormField :label="t(`profile.fields.${field.name}`)" :error="userForm.errors[field.name]">
            <component
              :is="fieldComponent(field)"
              v-model="userForm[field.name]"
              :options="field.type === 'enum' ? enumOptions(field) : undefined"
              option-label="label"
              option-value="value"
              class="w-full" />
          </InertiaFormField>
        </template>
      </div>

      <div class="mt-6 flex justify-end">
        <Button :label="t('actions.save')" :loading="savingText" type="submit" severity="primary" />
      </div>
    </form>
  </div>

  <!-- Password Change Form -->
  <div class="flex flex-col gap-8 rounded-xl border border-gray-200 bg-white p-18 shadow-sm">
    <SectionHeading class="mb-6">
      {{ t('titles.password') }}
    </SectionHeading>

    <form class="space-y-4" @submit.prevent="submitNewPassword">
      <!-- Trick to prevent Chrome autofill -->
      <input type="password" autocomplete="new-password" style="display: none" />

      <InertiaFormField :label="t('profile.fields.current_password')" :error="passwordForm.errors.current_password">
        <InputText
          v-model="passwordForm.current_password"
          type="password"
          class="w-full"
          autocomplete="current-password"
          placeholder="••••••••••••"
          :class="{ 'p-invalid': passwordForm.errors.current_password }" />
      </InertiaFormField>

      <InertiaFormField :label="t('profile.fields.new_password')" :error="passwordForm.errors.new_password">
        <InputText
          v-model="passwordForm.new_password"
          type="password"
          class="w-full"
          autocomplete="new-password"
          :class="{ 'p-invalid': passwordForm.errors.new_password }" />
      </InertiaFormField>

      <InertiaFormField
        :label="t('profile.fields.new_password_confirmation')"
        :error="passwordForm.errors.new_password_confirmation">
        <InputText
          v-model="passwordForm.new_password_confirmation"
          type="password"
          class="w-full"
          autocomplete="new-password"
          :class="{ 'p-invalid': passwordForm.errors.new_password_confirmation }" />
      </InertiaFormField>

      <div class="mt-6 flex justify-end">
        <Button
          type="submit"
          :label="t('actions.save_password')"
          severity="primary"
          :loading="passwordForm.processing" />
      </div>
    </form>
  </div>
</template>

<i18n>
{
	"en": {
		"titles": {
			"user_profile": "User Profile",
			"profile_image": "Profile picture",
			"password": "Password"
		},
		"profile": {
			"fields": {
				"first_name": "First name",
				"last_name": "Last name",
				"email": "Email address",
				"language": "Language",
				"phone": "Phone number",
				"receive_app_notifications": "Receive notifications",
				"current_password": "Current password",
				"new_password": "New password",
				"new_password_confirmation": "New password (confirmation)",
				"passwords_do_not_match": "Passwords do not match"
			}
		},
		"actions": {
			"save": "Save",
			"save_password": "Save Password"
		},
    "warnings": {
      "current_password_required": "Please enter your current password.",
      "new_password_required": "Please enter a new password.",
      "password_confirmation_required": "Please confirm your new password.",
      "passwords_do_not_match": "The new passwords do not match."
    }
	},
	"de": {
		"titles": {
			"user_profile": "Nutzerprofil",
			"profile_image": "Profilbild",
			"password": "Passwort"
		},
		"profile": {
			"fields": {
				"first_name": "Vorname",
				"last_name": "Nachname",
				"email": "E-Mail-Adresse",
				"language": "Sprache",
				"phone": "Telefonnummer",
				"receive_app_notifications": "Benachrichtigungen erhalten",
				"current_password": "Aktuelles Passwort",
				"new_password": "Neues Passwort",
				"new_password_confirmation": "Neues Passwort (Wiederholung)",
				"passwords_do_not_match": "Passwörter stimmen nicht überein"
			}
		},
		"actions": {
			"save": "Speichern",
			"save_password": "Passwort speichern"
		},
    "warnings": {
      "current_password_required": "Bitte geben Sie Ihr aktuelles Passwort ein.",
      "new_password_required": "Bitte geben Sie ein neues Passwort ein.",
      "password_confirmation_required": "Bitte bestätigen Sie Ihr neues Passwort.",
      "passwords_do_not_match": "Die neuen Passwörter stimmen nicht überein."
    }
	}
}
</i18n>
